import React, { useState, useEffect } from 'react';
import './FeedbackWidget.css';

function FeedbackWidget({ userEmail }) {
  const [showModal, setShowModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('bug');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setFeedbackEmail(userEmail);
    }
  }, [userEmail]);

  // Close modal on Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [showModal]);

  const closeModal = () => {
    setShowModal(false);
    setSubmitted(false);
    setFeedbackText('');
    setFeedbackEmail(userEmail || '');
    setFeedbackType('bug');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim()) return;

    setSubmitting(true);

    try {
      const response = await fetch('https://formsubmit.co/ajax/feedback@mindmark.au', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: `Mindmark Feedback: ${feedbackType}`,
          type: feedbackType,
          message: feedbackText,
          email: feedbackEmail || 'Not provided',
          page: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send feedback');
      }

      setSubmitted(true);

      setTimeout(() => {
        closeModal();
      }, 3000);
    } catch (err) {
      alert(`Failed to send feedback: ${err.message}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        className="feedback-float-button"
        onClick={() => setShowModal(true)}
        aria-label="Send Feedback"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {showModal && (
        <div className="feedback-modal-overlay" onClick={closeModal}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <button className="feedback-modal-close" onClick={closeModal}>
              &#x2715;
            </button>

            {!submitted ? (
              <>
                <h2 className="feedback-modal-title">Send Feedback</h2>
                <form onSubmit={handleSubmit}>
                  <div className="feedback-type-selection">
                    <label className="feedback-radio-label">
                      <input
                        type="radio"
                        name="feedbackType"
                        value="bug"
                        checked={feedbackType === 'bug'}
                        onChange={(e) => setFeedbackType(e.target.value)}
                      />
                      <span>Report a Bug</span>
                    </label>
                    <label className="feedback-radio-label">
                      <input
                        type="radio"
                        name="feedbackType"
                        value="feature"
                        checked={feedbackType === 'feature'}
                        onChange={(e) => setFeedbackType(e.target.value)}
                      />
                      <span>Request a Feature</span>
                    </label>
                  </div>

                  <div className="feedback-form-group">
                    <label htmlFor="feedback-text">
                      {feedbackType === 'bug' ? 'Describe the bug:' : 'Describe your feature request:'}
                    </label>
                    <textarea
                      id="feedback-text"
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder={feedbackType === 'bug'
                        ? 'What happened? What did you expect to happen?'
                        : 'What feature would you like to see?'}
                      rows={5}
                      required
                      className="feedback-textarea"
                    />
                  </div>

                  <div className="feedback-form-group">
                    <label htmlFor="feedback-email">Email (optional):</label>
                    <input
                      type="email"
                      id="feedback-email"
                      value={feedbackEmail}
                      onChange={(e) => setFeedbackEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="feedback-input"
                    />
                  </div>

                  <button
                    type="submit"
                    className="feedback-submit-button"
                    disabled={!feedbackText.trim() || submitting}
                  >
                    {submitting ? 'Sending...' : 'Submit Feedback'}
                  </button>
                </form>
              </>
            ) : (
              <div className="feedback-thank-you">
                <div className="feedback-thank-you-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2>Thank You!</h2>
                <p>Your feedback has been received. We appreciate your help in improving Mindmark!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackWidget;
